/**
 * Compact FCFA display for KPI cards and stat tiles.
 *
 * Examples:
 *   10 000 000 → "10 M FCFA"
 *   1 500 000  → "1,5 M FCFA"
 *   500 000    → "500 k FCFA"
 *   12 500     → "12,5 k FCFA"
 *   800        → "800 FCFA"
 */
export function formatFcfa(amount) {
  const v = Number(amount) || 0
  if (v >= 1_000_000) {
    const m = v / 1_000_000
    const label = Number.isInteger(m) ? m : m.toFixed(1).replace('.', ',')
    return `${label} M FCFA`
  }
  if (v >= 1_000) {
    const k = v / 1_000
    const label = Number.isInteger(k) ? k : k.toFixed(1).replace('.', ',')
    return `${label} k FCFA`
  }
  return `${v.toLocaleString('fr-FR')} FCFA`
}

/**
 * Full FCFA display with thousand separators — for tables and detail views.
 * Example: 10 000 000 → "10 000 000 FCFA"
 */
export function formatFcfaFull(amount) {
  const v = Number(amount) || 0
  return `${v.toLocaleString('fr-FR')} FCFA`
}

/**
 * Compact number without unit — for mixed-suffix display.
 * Example: 1 500 000 → "1,5 M"
 */
export function formatCompact(amount) {
  const v = Number(amount) || 0
  if (v >= 1_000_000) {
    const m = v / 1_000_000
    return Number.isInteger(m) ? `${m} M` : `${m.toFixed(1).replace('.', ',')} M`
  }
  if (v >= 1_000) {
    const k = v / 1_000
    return Number.isInteger(k) ? `${k} k` : `${k.toFixed(1).replace('.', ',')} k`
  }
  return v.toLocaleString('fr-FR')
}

/**
 * Format phone number with readable spaces (e.g. +226 70 12 34 56).
 */
export function formatPhone(phone) {
  if (!phone) return '—'
  let p = String(phone).trim().replace(/\s+/g, '')
  if (p.startsWith('00226')) p = '+226' + p.slice(5)
  else if (p.startsWith('226') && !p.startsWith('+')) p = '+' + p
  else if (!p.startsWith('+') && p.length === 8) p = '+226' + p

  if (p.startsWith('+226') && p.length === 12) {
    const local = p.slice(4)
    return `+226 ${local.slice(0, 2)} ${local.slice(2, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)}`
  }
  return p
}

/**
 * Matches a query against a Burkina Faso phone number respecting 2-digit block boundaries (e.g. 76 67 49 68).
 * Prevents false positives across block boundaries (e.g. searching '74' must NOT match '76 67 49 68').
 */
export function matchBurkinaPhone(phone, query) {
  if (!phone || !query) return false
  const pDigits = String(phone).replace(/\D/g, '')
  const qDigits = String(query).replace(/\D/g, '')
  if (!qDigits) return false

  // If query specifies international prefix (+226 or 226)
  if (qDigits.startsWith('226')) {
    return pDigits.includes(qDigits)
  }

  // Extract the local 8 digits (e.g. 76674968)
  const local = pDigits.startsWith('226') ? pDigits.slice(3) : pDigits

  // If not standard 8 digits, fallback to normal include
  if (local.length !== 8) {
    return local.includes(qDigits)
  }

  // Burkina numbers are structured in 4 pairs: [0..2, 2..4, 4..6, 6..8]
  // The query digits must start on a 2-digit block boundary (offsets 0, 2, 4, 6)
  const blockOffsets = [0, 2, 4, 6]
  for (const offset of blockOffsets) {
    const sub = local.slice(offset)
    if (sub.startsWith(qDigits)) {
      return true
    }
  }

  return false
}


